// src/components/jobPost/steps/Step4PaymentOffering.jsx
import { useState } from "react";
import { Card, CardContent } from "../../../ui/card";
import { Label } from "../../../ui/label";
import { Input } from "../../../ui/input";
import { Checkbox } from "../../../ui/checkbox";
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { StepFooterNav } from "../../shared/NavigationFooter";

export function Step4({ currentStep, totalSteps, nextStep, preStep }) {
  const { jobPostProgress, setPaymentOffering, setIsRequestedForVolunteer } =
    useJobPostProgressContext();

  const [amount, setAmount] = useState(jobPostProgress.paymentOffering || "");
  const [error, setError] = useState("");

  const handleAmountChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/[^0-9.]/g, "");
    if (/^\d*\.?\d{0,2}$/.test(inputValue)) {
      setAmount(inputValue);
      setError("");
    }
  };

  const handleBlur = () => {
    const numericValue = parseFloat(amount);
    if (!isNaN(numericValue)) {
      setAmount(numericValue.toFixed(2));
    } else {
      setAmount("");
    }
  };

  const handleVolunteerChange = (checked) => {
    setIsRequestedForVolunteer(checked);
    if (checked) {
      setAmount("3.00");
      setPaymentOffering("3.00");
      setError("");
    } else {
      setAmount("");
      setPaymentOffering("");
    }
  };

  const handleNext = () => {
    const numericAmount = parseFloat(amount.replace("$", ""));
    if (!numericAmount || numericAmount <= 0) {
      setError("Please enter a valid payment offering.");
      return;
    }
    setPaymentOffering(numericAmount.toFixed(2));
    nextStep();
  };

  return (
    <div className="space-y-12 py-5 md:py-10">
      {/* Title */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl md:text-5xl font-bold text-black">Payment Offering</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Set how much you want to pay for this service
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        <Card className="p-6 lg:p-8">
          <CardContent className="p-0 space-y-6">
            {/* Payment Input */}
            <div className="space-y-4">
              <Label className="text-xl">Payment Amount</Label>
              <div className="relative">
                <span className="absolute left-4 top-4 text-2xl text-cyan-700">
                  $
                </span>
                <Input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  onBlur={handleBlur}
                  placeholder="15.00"
                  className="pl-8 h-16 text-2xl sm:text-2xl md:text-2xl lg:text-2xl font-bold text-cyan-700 placeholder:text-cyan-700/50"
                />
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 p-6 rounded-xl text-base text-gray-700">
              The amount will be authorized and will not be charged until you
              are matched with a helper and confirm the service is complete for same day service. The amount will be charged immediately if it is scheduled for a future date.
            </div>

            {/* Volunteer Toggle */}
            <div className="flex items-center space-x-3">
              <Checkbox
                id="volunteer"
                checked={jobPostProgress.isRequestedForVolunteer}
                onCheckedChange={handleVolunteerChange}
              />
              <Label htmlFor="volunteer" className="text-sm font-medium">
                Are you looking for a volunteer? ($3 will be charged to help maintain the platform)
              </Label>
            </div>

            {/* Pricing Guide */}
            <div className="mt-6 text-black space-y-6">
              <p className="text-base">
                Pay what you want but we also have <span className="italic font-semibold underline">suggested</span> pricing
              </p>

              {[
                {
                  title: "Lawn Mowing or Leaf Raking",
                  items: [
                    { label: "Small (30 mins or less)", price: "$15 - $30" },
                    { label: "Medium (30 to 60 mins)", price: "$20 - $60" },
                    { label: "Long (60 to 90 mins)", price: "$40 - $100" },
                    { label: "Extra Long (90+ mins)", price: "$60+" },
                  ],
                },
                {
                  title: "Snow Shoveling",
                  items: [
                    { label: "Single Car Driveway", price: "$15 - $40" },
                    { label: "Double Car Driveway", price: "$20 - $60" },
                    { label: "Triple Car Driveway", price: "$40 - $100" },
                  ],
                },
              ].map((section, i) => (
                <div key={i} className="space-y-3">
                  <h2 className="font-bold text-lg">{section.title}</h2>
                  <ul className="space-y-2">
                    {section.items.map((item, j) => (
                      <li key={j} className="text-sm">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                          <span className="font-normal text-gray-700">{item.label}</span>
                          <span className="text-base font-medium text-cyan-700">{item.price}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Navigation */}
      <StepFooterNav
        currentStep={currentStep}
        totalSteps={totalSteps}
        onBack={preStep}
        onNext={handleNext}
      />
    </div>
  );
}